import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Search, Brain, Zap, Coffee, Rocket, Lightbulb, Target, Mail, Calendar, FileText, MessageSquare } from 'lucide-react';

interface TypingIndicatorProps {
  context?: string; // The user's message for context
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ context }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [showDots, setShowDots] = useState(true);

  // Function to detect request type from context
  const getRequestType = (text: string): string => {
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('research') || lowerText.includes('find') || lowerText.includes('search') || lowerText.includes('look up')) {
      return 'research';
    }
    if (lowerText.includes('email') || lowerText.includes('send') || lowerText.includes('mail')) {
      return 'email';
    }
    if (lowerText.includes('meeting') || lowerText.includes('schedule') || lowerText.includes('calendar')) {
      return 'meeting';
    }
    if (lowerText.includes('project') || lowerText.includes('create') || lowerText.includes('task')) {
      return 'project';
    }
    if (lowerText.includes('reminder') || lowerText.includes('remind')) {
      return 'reminder';
    }
    return 'general';
  };

  // Get context-aware messages
  const getContextualMessages = (requestType: string) => {
    const generalMessages = [
      {
        text: "🔍 Sair is digging through the digital archives...",
        icon: Search,
        color: "text-blue-600"
      },
      {
        text: "🧠 Sair is processing with advanced AI brain cells...",
        icon: Brain,
        color: "text-purple-600"
      },
      {
        text: "⚡ Sair is charging up knowledge capacitors...",
        icon: Zap,
        color: "text-yellow-600"
      },
      {
        text: "☕ Sair is taking a quick break while thinking...",
        icon: Coffee,
        color: "text-orange-600"
      },
      {
        text: "🚀 Sair is launching research rockets...",
        icon: Rocket,
        color: "text-red-600"
      },
      {
        text: "💡 Sair is having a brilliant idea moment...",
        icon: Lightbulb,
        color: "text-yellow-500"
      },
      {
        text: "🎯 Sair is aiming for the perfect answer...",
        icon: Target,
        color: "text-green-600"
      },
      {
        text: "🔬 Sair is running some AI experiments...",
        icon: Brain,
        color: "text-indigo-600"
      },
      {
        text: "📚 Sair is consulting the digital library...",
        icon: Search,
        color: "text-blue-500"
      },
      {
        text: "⚙️ Sair is fine-tuning the response engine...",
        icon: Zap,
        color: "text-gray-600"
      }
    ];

    const researchMessages = [
      {
        text: "🔍 Sair is scouring the internet for the latest info...",
        icon: Search,
        color: "text-blue-600"
      },
      {
        text: "📊 Sair is analyzing market trends and data...",
        icon: Brain,
        color: "text-purple-600"
      },
      {
        text: "🌐 Sair is surfing the web waves...",
        icon: Search,
        color: "text-blue-500"
      },
      {
        text: "📈 Sair is crunching numbers and statistics...",
        icon: Target,
        color: "text-green-600"
      },
      {
        text: "🔬 Sair is conducting digital research...",
        icon: Brain,
        color: "text-indigo-600"
      }
    ];

    const emailMessages = [
      {
        text: "✉️ Crafting the perfect email...",
        icon: Mail,
        color: "text-blue-600"
      },
      {
        text: "📝 Writing with style and clarity...",
        icon: FileText,
        color: "text-purple-600"
      },
      {
        text: "🎨 Adding professional polish...",
        icon: Lightbulb,
        color: "text-yellow-500"
      },
      {
        text: "📧 Composing your message...",
        icon: Mail,
        color: "text-blue-500"
      },
      {
        text: "✨ Making it shine...",
        icon: Zap,
        color: "text-yellow-600"
      }
    ];

    const meetingMessages = [
      {
        text: "📅 Checking calendar availability...",
        icon: Calendar,
        color: "text-blue-600"
      },
      {
        text: "⏰ Finding the perfect time slot...",
        icon: Calendar,
        color: "text-green-600"
      },
      {
        text: "🤝 Coordinating schedules...",
        icon: Calendar,
        color: "text-purple-600"
      },
      {
        text: "📋 Preparing meeting details...",
        icon: FileText,
        color: "text-blue-500"
      }
    ];

    const projectMessages = [
      {
        text: "📋 Setting up your project...",
        icon: FileText,
        color: "text-blue-600"
      },
      {
        text: "🎯 Defining project goals...",
        icon: Target,
        color: "text-green-600"
      },
      {
        text: "📝 Creating project structure...",
        icon: FileText,
        color: "text-purple-600"
      },
      {
        text: "⚙️ Configuring project settings...",
        icon: Zap,
        color: "text-yellow-600"
      }
    ];

    const reminderMessages = [
      {
        text: "⏰ Setting up your reminder...",
        icon: Calendar,
        color: "text-blue-600"
      },
      {
        text: "🔔 Programming notification...",
        icon: MessageSquare,
        color: "text-green-600"
      },
      {
        text: "📱 Syncing with your devices...",
        icon: Zap,
        color: "text-purple-600"
      },
      {
        text: "🎯 Making sure you won't forget...",
        icon: Target,
        color: "text-orange-600"
      }
    ];

    switch (requestType) {
      case 'research':
        return researchMessages;
      case 'email':
        return emailMessages;
      case 'meeting':
        return meetingMessages;
      case 'project':
        return projectMessages;
      case 'reminder':
        return reminderMessages;
      default:
        return generalMessages;
    }
  };

  const requestType = context ? getRequestType(context) : 'general';
  const engagingMessages = getContextualMessages(requestType);

  useEffect(() => {
    // Rotate messages every 3 seconds
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % engagingMessages.length);
    }, 3000);

    // Toggle dots every 1.5 seconds
    const dotsInterval = setInterval(() => {
      setShowDots((prev) => !prev);
    }, 1500);

    return () => {
      clearInterval(messageInterval);
      clearInterval(dotsInterval);
    };
  }, [engagingMessages.length]);

  const currentMessage = engagingMessages[currentMessageIndex];
  const IconComponent = currentMessage.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex justify-start"
    >
      <div className="flex items-start space-x-4 max-w-xs md:max-w-md lg:max-w-lg">
        {/* AI Avatar */}
        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
          <Bot className="w-5 h-5 text-white" />
        </div>

        {/* Typing Bubble */}
        <div className="message-bubble ai-message shadow-xl">
          <div className="flex items-center space-x-3">
            {/* Icon */}
            <IconComponent className={`w-5 h-5 ${currentMessage.color}`} />
            
            {/* Message */}
            <span className="text-sm text-modern-700 font-medium">
              {currentMessage.text}
            </span>
            
            {/* Animated dots */}
            <AnimatePresence mode="wait">
              {showDots && (
                <motion.div
                  key="dots"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex space-x-1"
                >
            <motion.div
                    className="w-2 h-2 bg-primary-500 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
            />
            <motion.div
                    className="w-2 h-2 bg-primary-500 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
            />
            <motion.div
                    className="w-2 h-2 bg-primary-500 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
            />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TypingIndicator; 